import { useState, useRef } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Upload, X, Loader2, Link as LinkIcon } from 'lucide-react'
import { uploadImage } from '@/services/storage'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'

interface ImageUploadProps {
  value?: string | null
  onChange: (url: string | null) => void
  className?: string
  bucket?: string
  label?: string
}

export const ImageUpload = ({
  value,
  onChange,
  className,
  bucket = 'images',
  label = 'Upload Image',
}: ImageUploadProps) => {
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    // Validate type
    if (!file.type.startsWith('image/')) {
      toast({
        variant: 'destructive',
        title: 'Invalid file type',
        description: 'Please upload an image file (PNG, JPG, WEBP).',
      })
      return
    }

    // Validate size (e.g. 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: 'destructive',
        title: 'File too large',
        description: 'Maximum file size is 5MB.',
      })
      return
    }

    try {
      setIsUploading(true)
      const url = await uploadImage(file, bucket)
      onChange(url)
      toast({
        title: 'Image uploaded',
        description: 'Image has been successfully uploaded.',
      })
    } catch (error) {
      // Safe error logging - extracting only serializable message
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error'
      console.error('Upload failed:', errorMessage)

      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: errorMessage,
      })
    } finally {
      setIsUploading(false)
      // Reset input so same file can be selected again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const handleRemove = () => {
    onChange(null)
  }

  return (
    <div className={cn('space-y-3', className)}>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        accept="image/png, image/jpeg, image/jpg, image/webp"
      />

      {!value ? (
        <div
          className="border-2 border-dashed border-slate-200 rounded-lg p-6 flex flex-col items-center justify-center gap-2 hover:bg-slate-50 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <div className="p-3 bg-slate-100 rounded-full">
            {isUploading ? (
              <Loader2 className="h-6 w-6 text-slate-500 animate-spin" />
            ) : (
              <Upload className="h-6 w-6 text-slate-500" />
            )}
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-slate-700">
              {isUploading ? 'Uploading...' : label}
            </p>
            <p className="text-xs text-slate-500 mt-1">
              PNG, JPG, WEBP up to 5MB
            </p>
          </div>
        </div>
      ) : (
        <div className="relative rounded-lg overflow-hidden border bg-slate-50 aspect-video group">
          <img
            src={value}
            alt="Preview"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Change'
              )}
            </Button>
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={handleRemove}
              disabled={isUploading}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Manual URL Input */}
      <div className="flex items-center gap-2">
        <LinkIcon className="w-4 h-4 text-slate-400 shrink-0" />
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="Or paste image URL here..."
          className="h-9 text-xs font-mono"
        />
      </div>
    </div>
  )
}
